import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../Footer';
import { Button } from '../ui/button';
import MemoryTypesBadges from '../ui/MemoryTypesBadges';
import IntegrationScreenshots from './IntegrationScreenshots';

const FEATURES_VISIBLE_DEFAULT = 4;

export interface IntegrationLandingBaseProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features?: Array<{ icon: React.ReactNode; title: string; description: string }>;
  setupSteps?: string[];
  ctas?: Array<{ label: string; onClick: () => void; className?: string; icon?: React.ReactNode; variant?: string; size?: string }>;
  screenshots?: Array<{ id: string; title: string; description: string; imageUrl?: string; placeholder?: string; badge?: string; storyName?: string }>;
  videoUrl?: string;
  microcopy?: string;
  trustedBy?: string;
  testimonial?: string;
  /** Registry id for memory modality badges (text/audio/image/video). */
  registryIntegrationId?: string;
  children?: React.ReactNode;
}

const getYouTubeEmbedUrl = (url: string) => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : '';
};

const IntegrationLandingBase: React.FC<IntegrationLandingBaseProps> = ({
  icon,
  title,
  description,
  features = [],
  setupSteps = [],
  ctas = [],
  screenshots = [],
  videoUrl,
  microcopy,
  trustedBy,
  testimonial,
  registryIntegrationId,
  children,
}) => {
  const { t } = useTranslation('integrations');
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const visibleFeatures = showAllFeatures ? features : features.slice(0, FEATURES_VISIBLE_DEFAULT);
  const hasMoreFeatures = features.length > FEATURES_VISIBLE_DEFAULT;

  return (
    <div className="min-h-screen bg-white font-outer flex flex-col justify-between">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-white pt-20 pb-12">
          <div className="container mx-auto px-4 text-center">
            <div className="flex flex-col items-center justify-center gap-4 mb-8">
              <div className="mb-2">{icon}</div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-2 leading-tight">
                {title}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-2">
                {description}
              </p>
              {microcopy && <div className="text-indigo-600 font-medium text-base mb-2">{microcopy}</div>}
              {trustedBy && <div className="text-xs text-gray-400 mb-2">{trustedBy}</div>}
            </div>

            {registryIntegrationId && (
              <div className="w-full max-w-2xl mx-auto px-4 mb-8 text-left">
                <h2 className="text-sm font-semibold text-gray-800 mb-3 text-center">
                  {t('memoryModalities.sectionTitle')}
                </h2>
                <div className="flex justify-center border border-gray-200 rounded-xl bg-gray-50 px-4 py-4">
                  <MemoryTypesBadges integrationId={registryIntegrationId} className="justify-center max-w-full" />
                </div>
              </div>
            )}

            {ctas.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                {ctas.map((cta, idx) => (
                  <Button
                    key={cta.label}
                    onClick={cta.onClick}
                    className={cta.className}
                    variant={(cta.variant as any) ?? (idx === 0 ? 'default' : 'outline')}
                    size={(cta.size as any) ?? 'lg'}
                  >
                    {cta.icon && <span className="mr-2">{cta.icon}</span>}
                    {cta.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Screenshots Section */}
        {screenshots.length > 0 && (
          <IntegrationScreenshots screenshots={screenshots} />
        )}

        {/* Video Section (collapsible) */}
        {videoUrl && (
          <section className="bg-white py-8">
            <div className="container mx-auto px-4 flex flex-col items-center">
              {!showVideo ? (
                <Button variant="outline" size="lg" onClick={() => setShowVideo(true)}>
                  Watch setup video
                </Button>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Watch Demo</h2>
              <div className="w-full max-w-3xl aspect-video rounded-xl overflow-hidden shadow-lg border border-gray-200 mb-4">
                <iframe
                  src={getYouTubeEmbedUrl(videoUrl)}
                  title="Integration Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>
              {testimonial && (
                <blockquote className="italic text-gray-500 max-w-xl mx-auto mt-2">“{testimonial}”</blockquote>
              )}
                </>
              )}
            </div>
          </section>
        )}

        {/* Features Section (3–4 visible by default, then "See all features") */}
        {features.length > 0 && (
          <section className="bg-gray-50 py-16">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {visibleFeatures.map((f, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm p-6 flex flex-col items-center text-center border border-gray-100">
                    <div className="mb-4">{f.icon}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                    <p className="text-gray-600 text-base">{f.description}</p>
                  </div>
                ))}
              </div>
              {hasMoreFeatures && !showAllFeatures && (
                <div className="text-center mt-8">
                  <Button variant="outline" onClick={() => setShowAllFeatures(true)}>
                    See all features
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Setup Steps Section */}
        {setupSteps.length > 0 && (
          <section className="bg-white py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Get Started in Minutes</h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                {setupSteps.map((step, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4">
                      {i + 1}
                    </div>
                    <p className="text-base text-gray-700 font-medium max-w-xs">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Custom Content */}
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default IntegrationLandingBase; 